import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import Map from "../../components/Map/index";
import Layout from "../../components/Layout";
import PropertyRoll from "../../components/PropertyRoll";
import PropertySearch from "../../components/PropertySearch";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { graphql, useStaticQuery } from "gatsby";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      maxWidth: "100vw",
    },
  })
);

const Result = ({ location }): ReactElement => {
  const classes = useStyles();
  const [filterOptions, setFilterOptions] =
    location.state?.filterOptions ?
      React.useState(location.state.filterOptions) :
      React.useState({
        sell: "Buy",
        beds: "",
        minPrice: "",
        maxPrice: "",
        STC: false,
        propertyType: "",
        sortProperty: "highest"
      });

  const [properties, setProperties] = React.useState([]);

  const [page, setPage] = useState(1);

  const query = useStaticQuery(graphql`
  query S1earchQuery {
    allMarkdownRemark(
      filter: { frontmatter: { templateKey: { eq: "property-post" } } }
    ) {
      edges {
        node {
          excerpt(pruneLength: 400)
          id
          fields {
            slug
          }
          frontmatter {
            id
            title
            templateKey
            summary
            addressPostcode
            address
            addressSa1
            bathrooms
            bedrooms
            garages
            gardens
            date
            latitude
            leaseType
            longitude
            otherrooms
            parkingSpaces
            price
            poa
            pricetext
            propertyType
            receptions
            sold
            dateSTC
            UO_LA
            rentalperiod
            featuredimage {
              childImageSharp {
                fluid(maxWidth: 640, quality: 64) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
  }
`);

  let res = query.allMarkdownRemark.edges;

  function filterPropertyBySell() {
    res = res.filter(p => {
      switch (filterOptions.sell) {
        case "Buy":
          return (
            p.node.frontmatter.rentalperiod < 2 ||
            !p.node.frontmatter.rentalperiod
          );
        case "Rent":
          return p.node.frontmatter.rentalperiod >= 2;
        default:
          break;
      }
    });
  }

  function filterPropertyByBeds() {
    if (filterOptions.beds) res = res.filter((r) => r.node.frontmatter.bedrooms >= +filterOptions.beds);
  }

  function filterPropertyByPrice() {
    if (filterOptions.minPrice || filterOptions.maxPrice) {
      res = res.filter((r) => {
        let reducesMinPrice = +r.node.frontmatter.price;
        let reducedMaxPrice = +r.node.frontmatter.price;
        if (
          r.node.frontmatter.rentalperiod > 1 &&
          r.node.frontmatter.rentalperiod < 4
        ) {
          reducesMinPrice *= 4;
          reducedMaxPrice *= 4;
        }
        return (
          (reducesMinPrice > filterOptions.minPrice || !filterOptions.minPrice) &&
          (reducedMaxPrice < filterOptions.maxPrice || !filterOptions.maxPrice)
        );
      });
    }
  }

  function filterPropertyByType() {
    // property type array of flat or apartments
    const flatPropertyTypes = [9, 10, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 58, 59, 67, 68, 71, 72];
    if (filterOptions.propertyType) {
      if (filterOptions.propertyType !== "All") {
        res = +filterOptions.propertyType > 0 ?
          res.filter(
            (r) => flatPropertyTypes.indexOf(r.node.frontmatter.propertyType) < 0
          ) :
          res.filter(
            (r) => flatPropertyTypes.indexOf(r.node.frontmatter.propertyType) >= 0
          )
      }
    }
  }

  function filterPropertyBySTC() {
    if (!filterOptions.STC) res = res.filter(r => r.node.frontmatter.sold !== 2 && !r.node.frontmatter.UO_LA);
  }

  function sortProperty() {
    res?.sort((a, b) => {
      switch (filterOptions.sortProperty) {
        case "lowest":
          return a.node.frontmatter.price - b.node.frontmatter.price;
        case "highest":
          return b.node.frontmatter.price - a.node.frontmatter.price;
        case "newest":
          return (
            new Date(b.node.frontmatter.date.split("Z").join("")).getTime() -
            new Date(a.node.frontmatter.date.split("Z").join("")).getTime()
          );
      }
    })
  };

  let locationQuery = typeof window !== "undefined" ? window.location.search : "";

  useEffect(() => {
    const sell = locationQuery.slice(1);
    if (sell === 'Buy') {
      setFilterOptions({
        beds: "",
        minPrice: "",
        maxPrice: "",
        STC: false,
        propertyType: "",
        sortProperty: "highest",
        sell: "Buy"
      });
    } else if (sell === 'Rent') {
      setFilterOptions({
        beds: "",
        minPrice: "",
        maxPrice: "",
        STC: false,
        propertyType: "",
        sortProperty: "newest",
        sell: "Rent"
      });
    }
  }, [locationQuery])

  useEffect(() => {
    filterPropertyBySell();
    filterPropertyByBeds();
    filterPropertyByPrice();
    filterPropertyByType();
    filterPropertyBySTC();
    sortProperty();
    setProperties(res);
  }, [
    filterOptions.sell,
    filterOptions.beds,
    filterOptions.minPrice,
    filterOptions.maxPrice,
    filterOptions.propertyType,
    filterOptions.sortProperty,
    filterOptions.STC,
  ]);

  const markers = React.useMemo(
    () =>
      properties
        ? properties.map((res) => ({
          geometry: {
            coordinates: [
              res.node.frontmatter.longitude,
              res.node.frontmatter.latitude,
            ],
          },
          properties: {
            id: res.node.frontmatter.id,
            title: res.node.frontmatter.address,
            price:
              res.node.frontmatter.poa ? 'P.O.A' :
                res.node.frontmatter.pricetext ?
                  res.node.frontmatter.pricetext + " " +
                  (res.node.frontmatter.rentalperiod < 2
                    ? new Intl.NumberFormat("en-UK", {
                      style: "currency",
                      useGrouping: true,
                      maximumSignificantDigits: 21,
                      currency: "GBP",
                    }).format(res.node.frontmatter.price)
                    : new Intl.NumberFormat("en-UK", {
                      style: "currency",
                      useGrouping: true,
                      maximumSignificantDigits: 21,
                      currency: "GBP",
                    }).format(
                      res.node.frontmatter.rentalperiod === 4
                        ? Math.round(res.node.frontmatter.price)
                        : Math.round(res.node.frontmatter.price * 52 / 12)
                    ) +
                    "pcm" +
                    "" +
                    new Intl.NumberFormat("en-UK", {
                      style: "currency",
                      useGrouping: true,
                      maximumSignificantDigits: 21,
                      currency: "GBP",
                    }).format(
                      res.node.frontmatter.rentalperiod === 4
                        ? Math.round(res.node.frontmatter.price * 12 / 52)
                        : Math.round(res.node.frontmatter.price)
                    ) +
                    "pw")
                  : res.node.frontmatter.rentalperiod < 2
                    ? new Intl.NumberFormat("en-UK", {
                      style: "currency",
                      useGrouping: true,
                      maximumSignificantDigits: 21,
                      currency: "GBP",
                    }).format(res.node.frontmatter.price)
                    : new Intl.NumberFormat("en-UK", {
                      style: "currency",
                      useGrouping: true,
                      maximumSignificantDigits: 21,
                      currency: "GBP",
                    }).format(
                      res.node.frontmatter.rentalperiod === 4
                        ? Math.round(res.node.frontmatter.price)
                        : Math.round(res.node.frontmatter.price * 52 / 12)
                    ) +
                    "pcm" +
                    " " +
                    new Intl.NumberFormat("en-UK", {
                      style: "currency",
                      useGrouping: true,
                      maximumSignificantDigits: 21,
                      currency: "GBP",
                    }).format(
                      res.node.frontmatter.rentalperiod === 4
                        ? Math.round(res.node.frontmatter.price * 12 / 52)
                        : Math.round(res.node.frontmatter.price)
                    ) +
                    "pw",
            image: res.node.frontmatter.featuredimage?.childImageSharp,
            dateSTC: res.node.frontmatter.dateSTC,
            UO_LA: res.node.frontmatter.UO_LA
          },
        }))
        : [],
    [properties]
  );

  function onSubmitFilters(filters) {
    setFilterOptions(filters);
  }

  return (
    <Layout pathname={location.pathname}>
      <main className={classes.root}>
        <div style={{ width: "100%", height: "30px", position: "relative", zIndex: 100, background: "#fafafa" }}></div>
        <div style={{ width: "100%", display: "flex" }}>
          <Map
            marginWrap="0"
            height={"60vh"}
            markers={markers}
            center={markers[0]?.geometry.coordinates || [-0.08203, 51.50162]}
            zoom={13}
          />
        </div>
        <div style={{ width: "100%", zIndex: 10, background: "#fafafa", position: "relative" }}>
          <div style={{ minWidth: "100vw", padding: 10, zIndex: 10 }}>
            <PropertySearch
              filterOptions={filterOptions}
              withSort={true}
              length={properties?.length}
              handleSubmitFilters={onSubmitFilters}
            />
          </div>
          <div style={{ maxWidth: "90vw", zIndex: 10, margin: "0 auto 32px" }}>
            <PropertyRoll
              length={properties?.length || 0}
              currentList={properties || []}
              page={page}
              setPage={setPage}
              properties={properties}
              filterOptions={filterOptions}
            />
          </div>
        </div>
      </main>
    </Layout>
  );
};

Result.propTypes = {
  location: PropTypes.object,
  search: PropTypes.object,
};

export default Result;
